import {
  CalculatorOutlined,
  PieChartOutlined,
  SnippetsOutlined,
  TeamOutlined,
  ToolOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { ComponentType } from "react";

import {
  PERMISSION_SCOPES,
  PermissionScope,
  TAX_ADVISORY_PERMISSION_SCOPES,
} from "@/modules/auth";
import { ONBOARDING_PERMISSION_SCOPES } from "@/modules/onboarding/permissions";
import TaxYearSelect from "../../pages/TaxDeclaration/components/TaxYearSelect";
import { LanguageToggle } from "./LanguageToggle";
import { NavigationLink } from "./LeftNavItem";
import { Logout } from "./Logout";

export type NavigationItem =
  | NavigationLink
  | {
      component: ComponentType;
      label: string;
      icon: ComponentType;
      requiredScope?: PermissionScope[];
      featureFlag?: string;
    };

export type NavigationConfig = Array<NavigationSection>;

export interface NavigationSection {
  icon?: ComponentType;
  label: string;
  children: NavigationItem[];
}

export const navigationItems: NavigationConfig = [
  {
    icon: TeamOutlined,
    label: "Mandanten",
    children: [
      {
        path: "/",
        label: "Alle Mandanten",
        requiredScope: [
          ...TAX_ADVISORY_PERMISSION_SCOPES,
          PERMISSION_SCOPES.DIY,
        ],
        preserveQueryParams: true,
      },
      {
        path: "/tax-account-view",
        label: "Steuerkontoabfrage",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/tax-registrations",
        label: "Steuererfassungen",
        preserveQueryParams: true,
      },
      {
        path: "/tax-service-view",
        label: "serviceTracker",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/command-hub",
        label: "Command Hub",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/integrations",
        label: "integrations",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
    ],
  },
  {
    icon: UserAddOutlined,
    label: "onboarding",
    children: [
      {
        path: "/onboarding",
        label: "onboarding",
        requiredScope: ONBOARDING_PERMISSION_SCOPES,
      },
    ],
  },
  {
    icon: CalculatorOutlined,
    label: "Accounting",
    children: [
      {
        path: "/transaction-view",
        label: "Transaction",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/belege-view",
        label: "Belege",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/receipt-overview",
        label: "Receipt overview",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/verification-view",
        label: "Verification",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/vat-view",
        label: "VAT",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/zm-view",
        label: "ZM",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/bwa-view",
        label: "BWA",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/rules-view",
        label: "Rules",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/feedback-view",
        label: "Feedback",
        requiredScope: [PERMISSION_SCOPES.TAX_SUPERVISOR],
      },
      {
        path: "/annotation-tag-view",
        label: "Annotation Tag",
        requiredScope: [PERMISSION_SCOPES.TAX_SUPERVISOR],
      },
    ],
  },
  {
    icon: PieChartOutlined,
    label: "Steuer",
    children: [
      {
        component: TaxYearSelect,
        label: "Year",
        requiredScope: [
          ...TAX_ADVISORY_PERMISSION_SCOPES,
          PERMISSION_SCOPES.DIY,
        ],
      },
      {
        path: "/tax-declaration/tax-home",
        label: "Steuer Home",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/tax-declaration/tax-preparation",
        label: "Vorbereitung",
        requiredScope: [
          ...TAX_ADVISORY_PERMISSION_SCOPES,
          PERMISSION_SCOPES.DIY,
        ],
        preserveQueryParams: true,
      },
      {
        path: "/tax-declaration/euer-declaration",
        label: "Anlage EÜR inkl. AV",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/tax-declaration/vat-annual-declaration",
        label: "Umsatzsteuererkl.",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/tax-declaration/trade-tax-declaration",
        label: "Gewerbesteuererkl.",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
      {
        path: "/tax-declaration/income-tax-declaration",
        label: "Einkommensteuererkl.",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
        preserveQueryParams: true,
      },
    ],
  },
  {
    icon: SnippetsOutlined,
    label: "Templates",
    children: [
      {
        path: "/anschreiben-template",
        label: "Anschreiben",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
      },
    ],
  },
  {
    icon: ToolOutlined,
    label: "Settings",
    children: [
      {
        path: "/service-tracker-export",
        label: "Leistungstracker-Export",
        /*
         * Narrower than the Operators entry next to it: running the monthly
         * billing is an ADMIN/CLERK job. This only HIDES the entry — the
         * endpoint's own scope guard is the enforcement.
         */
        requiredScope: [PERMISSION_SCOPES.ADMIN, PERMISSION_SCOPES.CLERK],
      },
      {
        path: "/operators",
        label: "Operators",
        requiredScope: TAX_ADVISORY_PERMISSION_SCOPES,
      },
      { component: Logout, label: "Logout" },
      { component: LanguageToggle, label: "Language" },
    ],
  },
];
